// caiet UC20-03 — the nomenclator synchronization moment and periodicity are
// stored as a five-field cron expression. The grammar lives here, in the one
// library both sides already depend on, because **two** processes have to agree
// on it: integrations-service decides when a source is due, and the Configurări
// screen must refuse an expression the sync task would refuse rather than let it
// travel to the server and come back as a raw CRON_EXPRESSION_INVALID (audit
// 2026-08-30 ST2, spec 002 T037). A second copy on the client is exactly the
// defect that audit found, so there is one implementation and no other.
//
// `cron-parser` is not a dependency and the two things this system needs from a
// parser — "is this expression valid" and (in integrations-service, over this
// grammar) "when does it fire next in Europe/Chisinau" — do not justify one.
type FieldName = 'minute' | 'hour' | 'dayOfMonth' | 'month' | 'dayOfWeek';

interface FieldRange {
  min: number;
  max: number;
}

const FIELD_RANGES: Record<FieldName, FieldRange> = {
  minute: { min: 0, max: 59 },
  hour: { min: 0, max: 23 },
  dayOfMonth: { min: 1, max: 31 },
  month: { min: 1, max: 12 },
  dayOfWeek: { min: 0, max: 7 },
};

const FIELD_ORDER: FieldName[] = ['minute', 'hour', 'dayOfMonth', 'month', 'dayOfWeek'];

export interface CronExpression {
  minutes: number[];
  hours: number[];
  daysOfMonth: number[];
  months: number[];
  daysOfWeek: number[];
  dayOfMonthRestricted: boolean;
  dayOfWeekRestricted: boolean;
}

function parseField(raw: string, range: FieldRange): number[] | null {
  const values = new Set<number>();
  for (const part of raw.split(',')) {
    const [spec, stepText] = part.split('/');
    if (spec === undefined || spec === '' || (stepText !== undefined && stepText === '')) {
      return null;
    }
    const step = stepText === undefined ? 1 : Number(stepText);
    if (!Number.isInteger(step) || step < 1) {
      return null;
    }
    let from: number;
    let to: number;
    if (spec === '*') {
      from = range.min;
      to = range.max;
    } else if (spec.includes('-')) {
      const [fromText, toText, ...rest] = spec.split('-');
      if (rest.length > 0 || fromText === undefined || toText === undefined) {
        return null;
      }
      from = Number(fromText);
      to = Number(toText);
    } else {
      from = Number(spec);
      to = from;
    }
    if (!Number.isInteger(from) || !Number.isInteger(to) || from > to) {
      return null;
    }
    if (from < range.min || to > range.max) {
      return null;
    }
    for (let value = from; value <= to; value += step) {
      values.add(value);
    }
  }
  return [...values].sort((a, b) => a - b);
}

export function parseCronExpression(expression: string): CronExpression | null {
  const fields = expression.trim().split(/\s+/);
  if (fields.length !== FIELD_ORDER.length) {
    return null;
  }
  const parsed: Partial<Record<FieldName, number[]>> = {};
  for (let i = 0; i < FIELD_ORDER.length; i += 1) {
    const name = FIELD_ORDER[i] as FieldName;
    const values = parseField(fields[i] as string, FIELD_RANGES[name]);
    if (values === null) {
      return null;
    }
    parsed[name] = values;
  }
  const daysOfWeek = [
    ...new Set((parsed.dayOfWeek as number[]).map((d) => (d === 7 ? 0 : d))),
  ].sort((a, b) => a - b);
  return {
    minutes: parsed.minute as number[],
    hours: parsed.hour as number[],
    daysOfMonth: parsed.dayOfMonth as number[],
    months: parsed.month as number[],
    daysOfWeek,
    dayOfMonthRestricted: (fields[2] as string) !== '*',
    dayOfWeekRestricted: (fields[4] as string) !== '*',
  };
}

export function isValidCronExpression(expression: string): boolean {
  return parseCronExpression(expression) !== null;
}
